import React, { FunctionComponent, useContext } from "react";
import { Button, CloseIconThin } from "@cmsgov/ds-medicare-gov";
import {
  DrugCoverageFilterOption,
  PlanSNPType,
  PlanType,
} from "../../../@types";
import { AppContext } from "../../../app/store";
import {
  doFiltersExist,
  isMedicareAdvantageType,
  useTranslate,
} from "../../../helpers";
import { updateFilter, updateSNPFilters } from "./helpers";
import { default as C } from "./constants";
import { createGa4Event, Ga4Event } from "../../../helpers/analytics";
import { useFlags } from "launchdarkly-react-client-sdk";

interface FilterChipsProps {
  pageReset: () => void;
}

const FilterChips: FunctionComponent<FilterChipsProps> = ({ pageReset }) => {
  const t = useTranslate();
  const context = useContext(AppContext);
  const {
    state,
    state: { searchResultsFilters, snpFilters, planType },
  } = context;
  const isMA = isMedicareAdvantageType(planType || PlanType.MAPD);
  const flags = useFlags();

  const convertFilterKeysToLabels = (filters: string[]) =>
    filters
      ? filters.reduce<{ key: string; label: string }[]>(
          (acc, filter) => [
            ...acc,
            {
              key: filter,
              label: t(C.filterLabelsByValue[filter]),
            },
          ],
          []
        )
      : [];

  const filterLabels = convertFilterKeysToLabels(
    searchResultsFilters.planCoveragesFilter
  );

  const handleFilterRemove = (filter: string): void => {
    createGa4Event({
      event_name: Ga4Event.FILTER_CLEARED,
      text: filter,
      state,
      flags,
    });

    updateFilter(
      context,
      C.SearchFilterKeys.planCoveragesFilter,
      searchResultsFilters.planCoveragesFilter.filter(f => f !== filter)
    );
    pageReset();
  };

  const filtersExist =
    doFiltersExist(searchResultsFilters) || (snpFilters?.length && isMA);

  return filtersExist ? (
    <ul
      className="FilterChips ds-c-list--bare ds-u-display--flex ds-u-flex-wrap--wrap"
      role="list"
    >
      {filterLabels.map(filter => (
        <li key={filter.key} role="listitem">
          <Button
            className="ds-c-button-icon--small"
            size="small"
            onClick={(): void => handleFilterRemove.call(null, filter.key)}
            data-testid={filter.key}
          >
            <span className="ds-u-visibility--screen-reader">
              {t("filter_labels.applied_aria")}
            </span>
            {filter.label}
            <CloseIconThin />
            <span className="ds-u-visibility--screen-reader">
              {t("filter_labels.aria")}
            </span>
          </Button>
        </li>
      ))}

      {searchResultsFilters.insuranceCarrierFilter && (
        <li key={searchResultsFilters.insuranceCarrierFilter}>
          <Button
            className="ds-c-button-icon--small"
            size="small"
            onClick={(): void => {
              createGa4Event({
                event_name: Ga4Event.FILTER_CLEARED,
                text: searchResultsFilters.insuranceCarrierFilter,
                state,
                flags,
              });

              updateFilter(
                context,
                C.SearchFilterKeys.insuranceCarrierFilter,
                ""
              );
              pageReset();
            }}
            data-testid={searchResultsFilters.insuranceCarrierFilter}
          >
            <span className="ds-u-visibility--screen-reader">
              {t("filter_labels.applied_aria")}
            </span>
            {searchResultsFilters.insuranceCarrierFilter}
            <CloseIconThin />
            <span className="ds-u-visibility--screen-reader">
              {t("filter_labels.aria")}
            </span>
          </Button>
        </li>
      )}

      {!!searchResultsFilters.starRatingFilter.length && (
        <li key={searchResultsFilters.starRatingFilter}>
          <Button
            className="ds-c-button-icon--small"
            size="small"
            onClick={(): void => {
              const eventText = `${searchResultsFilters.starRatingFilter} ${
                Number(searchResultsFilters.starRatingFilter) === 5
                  ? t("plan_terms.stars")
                  : t("plan_terms.stars_and_up")
              }`;

              createGa4Event({
                event_name: Ga4Event.FILTER_CLEARED,
                text: eventText,
                state,
                flags,
              });

              updateFilter(context, C.SearchFilterKeys.starRatingFilter, "");
              pageReset();
            }}
            data-testid={searchResultsFilters.starRatingFilter}
          >
            <span className="ds-u-visibility--screen-reader">
              {t("filter_labels.applied_aria")}
            </span>
            {searchResultsFilters.starRatingFilter}{" "}
            {Number(searchResultsFilters.starRatingFilter) === 5
              ? t("plan_terms.stars")
              : t("plan_terms.stars_and_up")}{" "}
            <CloseIconThin />
            <span className="ds-u-visibility--screen-reader">
              {t("filter_labels.aria")}
            </span>
          </Button>
        </li>
      )}

      {searchResultsFilters.drugCoverageFilter && (
        <li key={searchResultsFilters.drugCoverageFilter}>
          <Button
            className="ds-c-button-icon--small"
            size="small"
            onClick={(): void => {
              const eventText =
                searchResultsFilters.drugCoverageFilter ===
                DrugCoverageFilterOption.INCLUDES
                  ? t("search_results_page.includes_drug_coverage")
                  : t("search_results_page.excludes_drug_coverage");

              createGa4Event({
                event_name: Ga4Event.FILTER_CLEARED,
                text: eventText,
                state,
                flags,
              });

              updateFilter(
                context,
                C.SearchFilterKeys.drugCoverageFilter,
                undefined
              );
              pageReset();
            }}
            data-testid={searchResultsFilters.drugCoverageFilter}
          >
            <span className="ds-u-visibility--screen-reader">
              {t("filter_labels.applied_aria")}
            </span>
            {searchResultsFilters.drugCoverageFilter ===
            DrugCoverageFilterOption.INCLUDES
              ? t("search_results_page.includes_drug_coverage")
              : t("search_results_page.excludes_drug_coverage")}
            <CloseIconThin />
            <span className="ds-u-visibility--screen-reader">
              {t("filter_labels.aria")}
            </span>
          </Button>
        </li>
      )}

      {searchResultsFilters.planCategoriesFilter.map(cat => {
        const categoryName = t(C.categoryFilterTranslationMap[cat]);
        return (
          <li key={cat}>
            <Button
              className="ds-c-button-icon--small"
              size="small"
              onClick={(): void => {
                const selectedFilters = searchResultsFilters.planCategoriesFilter.filter(
                  f => f !== cat
                );

                createGa4Event({
                  event_name: Ga4Event.FILTER_CLEARED,
                  text: categoryName,
                  state,
                  flags,
                });

                updateFilter(
                  context,
                  C.SearchFilterKeys.planCategoriesFilter,
                  selectedFilters
                );
                pageReset();
              }}
              data-testid={cat}
            >
              <span className="ds-u-visibility--screen-reader">
                {t("filter_labels.applied_aria")}
              </span>
              {categoryName}
              <CloseIconThin />
              <span className="ds-u-visibility--screen-reader">
                {t("filter_labels.aria")}
              </span>
            </Button>
          </li>
        );
      })}

      {isMA &&
        snpFilters?.map(filter => {
          if (filter !== PlanSNPType.SNP_TYPE_NOT_SNP) {
            const filterName = t(C.snpFilterTranslationMap[filter]);

            return (
              <li key={filter}>
                <Button
                  className="ds-c-button-icon--small"
                  size="small"
                  onClick={(): void => {
                    const selectedFilters = snpFilters.filter(
                      f => f !== filter
                    );

                    createGa4Event({
                      event_name: Ga4Event.FILTER_CLEARED,
                      text: filterName,
                      state,
                      flags,
                    });

                    updateSNPFilters(context, selectedFilters);
                    pageReset();
                  }}
                  data-testid={filter}
                >
                  <span className="ds-u-visibility--screen-reader">
                    {t("filter_labels.applied_aria")}
                  </span>
                  {filterName}
                  <CloseIconThin />
                  <span className="ds-u-visibility--screen-reader">
                    {t("filter_labels.aria")}
                  </span>
                </Button>
              </li>
            );
          }
          return null;
        })}
    </ul>
  ) : null;
};

export default FilterChips;
